import { useRef } from "react";
import { Download, Award } from "lucide-react";
import { getStats, getLevel } from "../gamification";
import lessons from "../data/lessons.json";
import PremiumGate, { isPremium } from "../components/PremiumGate";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";

export default function Certificate() {
  const stats = getStats();
  const levelInfo = getLevel(stats.totalXp);
  const canvasRef = useRef(null);

  if (!isPremium()) return <PremiumGate feature="Proficiency Certificate" />;
  const currentDay = Number(localStorage.getItem("currentDay") || 1);
  const completedAll = currentDay > lessons.length;
  const user = JSON.parse(localStorage.getItem("currentUser") || "{}");

  function generateCertificate() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = 1100;
    canvas.height = 800;

    // Paper background
    ctx.fillStyle = "#fbf6e8";
    ctx.fillRect(0, 0, 1100, 800);

    // Cream-200 inner field
    ctx.fillStyle = "#f8efd3";
    ctx.fillRect(40, 40, 1020, 720);

    // Ink hairline frame
    ctx.strokeStyle = "#0b111d";
    ctx.lineWidth = 1;
    ctx.strokeRect(60, 60, 980, 680);

    // Gold inner frame
    ctx.strokeStyle = "#c89a2c";
    ctx.lineWidth = 2;
    ctx.strokeRect(80, 80, 940, 640);

    // Eyebrow
    ctx.fillStyle = "#a47d20";
    ctx.font = "bold 13px Manrope, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("SPEAKEASY · CERTIFICATE OF COMPLETION", 550, 130);

    // Title
    ctx.fillStyle = "#0b111d";
    ctx.font = "italic 56px 'Instrument Serif', serif";
    ctx.fillText("Awarded to", 550, 220);

    // Name
    ctx.font = "72px 'Instrument Serif', serif";
    ctx.fillText(user.name || "Learner", 550, 320);

    // Underline
    ctx.strokeStyle = "#c89a2c";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(280, 350);
    ctx.lineTo(820, 350);
    ctx.stroke();

    // Description
    ctx.fillStyle = "#2c3142";
    ctx.font = "16px Manrope, sans-serif";
    ctx.fillText("for steady completion of the SpeakEasy English-learning curriculum.", 550, 400);
    ctx.fillText(`${stats.totalXp} XP earned · ${stats.lessonsCompleted || 0} lessons completed · ${stats.streak}-day streak.`, 550, 430);

    // Level
    ctx.font = "italic 28px 'Instrument Serif', serif";
    ctx.fillStyle = "#c89a2c";
    ctx.fillText(completedAll ? "Course completed" : `${levelInfo.name}, Level ${levelInfo.level}`, 550, 500);

    // Date in mono
    ctx.font = "13px 'JetBrains Mono', monospace";
    ctx.fillStyle = "#5c6276";
    ctx.fillText(`Issued ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`, 550, 580);

    // Signature line
    ctx.strokeStyle = "#a4abb6";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(380, 650);
    ctx.lineTo(720, 650);
    ctx.stroke();
    ctx.fillStyle = "#5c6276";
    ctx.font = "12px Manrope, sans-serif";
    ctx.fillText("SpeakEasy Learning Platform · K2TechInfo", 550, 680);
  }

  function downloadCertificate() {
    generateCertificate();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `SpeakEasy-Certificate-${(user.name || "Learner").replace(/\s/g, "_")}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }

  return (
    <div className="page-enter" style={{ display: "flex", flexDirection: "column", gap: 24, paddingBottom: 48 }}>
      <div>
        <Chip tone="gold">Certificate</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          A small, <em style={{ color: "var(--gold-500)" }}>quiet</em> proof.
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, maxWidth: "60ch", lineHeight: 1.6, margin: 0 }}>
          Download a paper-toned PDF when you finish the curriculum — or earlier, with a level seal in the gold position.
        </p>
      </div>

      {/* Preview */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ background: "var(--cream-200)", padding: 48, textAlign: "center", borderBottom: "1px solid var(--border-2)" }}>
          <div style={{ background: "var(--paper-deep)", border: "1px solid var(--ink-900)", padding: 48, position: "relative" }}>
            <div style={{ position: "absolute", inset: 12, border: "1px solid var(--gold-500)", pointerEvents: "none" }} />
            <p className="eyebrow" style={{ marginBottom: 30 }}>Speakeasy · Certificate of Completion</p>
            <p className="font-display italic-display" style={{ fontSize: 36, fontStyle: "italic", margin: "0 0 8px", color: "var(--fg-2)" }}>Awarded to</p>
            <h2 className="font-display" style={{ fontSize: 56, fontWeight: 400, lineHeight: 1, margin: 0, color: "var(--ink-900)" }}>{user.name || "Learner"}</h2>
            <div style={{ width: "70%", height: 1, background: "var(--gold-500)", margin: "20px auto" }} />
            <p style={{ color: "var(--fg-2)", fontSize: 14, margin: "0 0 4px", lineHeight: 1.6, maxWidth: "44ch", marginLeft: "auto", marginRight: "auto" }}>
              for steady completion of the SpeakEasy English-learning curriculum.
            </p>
            <p style={{ color: "var(--fg-3)", fontSize: 13, margin: "0 0 18px" }}>
              {stats.totalXp} XP · {stats.lessonsCompleted || 0} lessons · {stats.streak}-day streak
            </p>
            <p className="italic-display" style={{ fontSize: 24, color: "var(--gold-700)", margin: "0 0 20px" }}>
              {completedAll ? "Course completed" : `${levelInfo.name}, level ${levelInfo.level}`}
            </p>
            <p className="mono" style={{ fontSize: 12, color: "var(--fg-3)", margin: 0 }}>
              Issued {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
        </div>

        <canvas ref={canvasRef} style={{ display: "none" }} />

        <div style={{ padding: 24, display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
          <p style={{ color: "var(--fg-2)", fontSize: 14, margin: 0 }}>
            <Award size={16} strokeWidth={1.5} style={{ display: "inline", verticalAlign: "middle", marginRight: 6, color: "var(--gold-600)" }} />
            Download a high-resolution PNG suitable for print.
          </p>
          <Button variant="primary" onClick={downloadCertificate} icon={<Download size={16} strokeWidth={1.8} />}>
            Download
          </Button>
        </div>
      </Card>

      {!completedAll && (
        <Card paperDeep style={{ background: "rgba(200,154,44,0.10)", borderColor: "rgba(200,154,44,0.30)" }}>
          <p className="italic-display" style={{ fontSize: 18, color: "var(--gold-700)", margin: "0 0 6px" }}>Almost there.</p>
          <p style={{ color: "var(--fg-2)", fontSize: 14, margin: 0, lineHeight: 1.55 }}>
            Complete the remaining {lessons.length - currentDay + 1} lessons to upgrade the seal from "Level {levelInfo.level}" to "Course completed".
          </p>
        </Card>
      )}
    </div>
  );
}
